import React, { useRef } from "react";
import { Grid, Paper, Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import OrderDetailsFetcher from "./TabletOrderDetails/TabletOrderData/OrderDetailsFetcher";
import ProductBrowser from "./TabletOrderDetails/ProductBrowser/ProductBrowesrMain";
import InvoiceCreator from "./TabletOrderDetails/TabletInvoiceCreator/InvoiceCreatorMain";

const TableModalContent = ({
  type,
  tableId,
  rootId,
  onSuccess,
  onClose,
  onOrderDeleted,
  orderId,
}) => {
  const reloadDetailsRef = useRef(null);
  const navigate = useNavigate();
  const scrollBoxRef = useRef(null);
  const theme = useTheme();

  // ✅ كشف دقيق لجميع أحجام التابلت والـ iPad
  // Small tablets: 7"-8" (Galaxy Tab A 8, iPad Mini Portrait)
  const isXSmallTablet = useMediaQuery("(min-width: 600px) and (max-width: 767px)");
  // Medium tablets: 9"-10.1" (iPad 9.7", Galaxy Tab S6 Lite)
  const isSmallTablet = useMediaQuery("(min-width: 768px) and (max-width: 834px)");
  // Large tablets: 10.5"-11" (iPad Air, iPad Pro 11", Galaxy Tab S7)
  const isMediumTablet = useMediaQuery("(min-width: 835px) and (max-width: 1024px)");
  // XL tablets: 12.9"+ (iPad Pro 12.9")
  const isLargeTablet = useMediaQuery("(min-width: 1025px) and (max-width: 1366px)");
  
  // ✅ كشف الوضع الأفقي/العمودي مع دقة أعلى
  const isPortrait = useMediaQuery("(orientation: portrait)");
  const isLandscape = useMediaQuery("(orientation: landscape)");

  // ✅ حساب المسافات بشكل ديناميكي حسب المقاس والاتجاه
  const getSpacing = () => {
    if (isXSmallTablet) return isPortrait ? 1 : 1.5;
    if (isSmallTablet) return isPortrait ? 1.5 : 2;
    if (isMediumTablet) return isPortrait ? 2 : 2.5;
    if (isLargeTablet) return 2.5;
    return 2.5;
  };

  const getPadding = () => {
    if (isXSmallTablet) return isPortrait ? 1.5 : 2;
    if (isSmallTablet) return isPortrait ? 2 : 2.5;
    if (isMediumTablet) return isPortrait ? 2.5 : 3;
    if (isLargeTablet) return 3;
    return 3;
  };

  // ✅ تحديد تخطيط الشبكة بناءً على المقاس والاتجاه
  const getGridBreakpoints = () => {
    // XSmall tablets Portrait: عمود واحد (Stack vertically)
    if (isXSmallTablet && isPortrait) {
      return { browser: 12, details: 12 };
    }
    // XSmall tablets Landscape: 65/35 split
    if (isXSmallTablet && isLandscape) {
      return { browser: 8, details: 4 };
    }
    // Small tablets Portrait: 55/45 split
    if (isSmallTablet && isPortrait) {
      return { browser: 12, details: 12 };
    }
    // Small tablets Landscape: 65/35 split
    if (isSmallTablet && isLandscape) {
      return { browser: 8, details: 4 };
    }
    // Medium tablets Portrait: عمود واحد أو split حسب الحاجة
    if (isMediumTablet && isPortrait) {
      return { browser: 12, details: 12 };
    }
    // Medium tablets Landscape: 70/30 split
    if (isMediumTablet && isLandscape) {
      return { browser: 8.5, details: 3.5 };
    }
    // Large tablets (iPad Pro 12.9"): 75/25 split
    if (isLargeTablet) {
      return isPortrait ? { browser: 12, details: 12 } : { browser: 9, details: 3 };
    }
    // Default fallback
    return { browser: 8, details: 4 };
  };

  const getScrollButtonSize = () => {
    if (isXSmallTablet) return 36;
    if (isSmallTablet) return 38;
    if (isMediumTablet) return 40;
    if (isLargeTablet) return 44;
    return 40;
  };
  
  const getBorderRadius = () => {
    if (isXSmallTablet) return 2;
    if (isSmallTablet) return 2.5;
    if (isMediumTablet) return 3;
    return 3;
  };

  const gridBreakpoints = getGridBreakpoints();
  const scrollButtonSize = getScrollButtonSize();
  const borderRadius = getBorderRadius();

  const handleScrollDown = () => {
    if (scrollBoxRef.current) {
      scrollBoxRef.current.scrollTop = scrollBoxRef.current.scrollHeight;
    }
  };

  // ✅ تحديد ما إذا كان يجب عرض التخطيط عمودياً
  const shouldStackVertically = 
    (isXSmallTablet && isPortrait) ||
    (isSmallTablet && isPortrait) ||
    (isMediumTablet && isPortrait) ||
    (isLargeTablet && isPortrait);

  return (
    <Grid
      container
      spacing={getSpacing()}
      sx={{
        // ✅ Flexbox محكم بدون overflow issues
        display: "flex",
        flexDirection: shouldStackVertically ? "column-reverse" : "row",
        alignItems: "stretch",
        minHeight: 0,
        width: "100%",
        maxWidth: "100%",
        margin: 0,
        padding: 0,
        // ✅ تحسين الأداء والتفاعل
        willChange: "transform",
        WebkitOverflowScrolling: "touch",
        touchAction: "manipulation",
        // ✅ منع overflow issues
        overflow: "hidden",
      }}
    >
      {/* Left Section - Product Browser */}
      <Grid
        item
        xs={12}
        md={gridBreakpoints.browser}
        sx={{
          order: shouldStackVertically ? 2 : 1,
          mb: shouldStackVertically ? getSpacing() : 0,
          mr: !shouldStackVertically ? getSpacing() / 2 : 0,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          // ✅ ضبط الارتفاع حسب الوضع
          height: shouldStackVertically ? "auto" : "100%",
          maxHeight: shouldStackVertically 
            ? (isXSmallTablet ? "50vh" : isSmallTablet ? "55vh" : "60vh")
            : "100%",
        }}
      >
        {type === "addItem" && orderId && (
          <ProductBrowser
            orderId={orderId}
            onSuccess={() => {
              if (typeof reloadDetailsRef.current === "function") {
                reloadDetailsRef.current();
              }
            }}
          />
        )}
      </Grid>

      {/* Right Section - Order Details */}
      <Grid
        item
        xs={12}
        md={gridBreakpoints.details}
        sx={{
          order: shouldStackVertically ? 1 : 2,
          mb: shouldStackVertically ? getSpacing() : 0,
          ml: !shouldStackVertically ? getSpacing() / 2 : 0,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: shouldStackVertically ? "auto" : "100%",
          maxHeight: shouldStackVertically
            ? (isXSmallTablet ? "45vh" : isSmallTablet ? "40vh" : "35vh")
            : "100%",
        }}
      >
        <Paper
          sx={{
            p: getPadding(),
            bgcolor: "#1e1e2f",
            color: "#fff",
            height: shouldStackVertically ? "100%" : "100%",
            minHeight: shouldStackVertically
              ? (isXSmallTablet ? "300px" : isSmallTablet ? "320px" : "350px")
              : "500px",
             display: "flex",
             flexDirection: "column",
             overflow: "hidden",
             position: "relative",
             borderRadius: borderRadius,
             boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
             border: "1px solid rgba(255, 255, 255, 0.05)",
             touchAction: "manipulation",
             WebkitTapHighlightColor: "transparent",
             transition: "all 0.3s ease",
           }}
        >
          <Box
            ref={scrollBoxRef}
            sx={{
              // ✅ Single scroll container
              flex: 1,
              minHeight: 0,
               overflowY: "auto",
               overflowX: "hidden",
              pr: isXSmallTablet ? 0.5 : isSmallTablet ? 0.75 : 1,
               scrollBehavior: "smooth",
               WebkitOverflowScrolling: "touch",
               // ✅ Custom scrollbar
               scrollbarWidth: "thin",
               scrollbarColor: "#fbc2eb #1a1a1a",
               "&::-webkit-scrollbar": {
                width: isXSmallTablet ? "4px" : isSmallTablet ? "5px" : "6px",
               },
               "&::-webkit-scrollbar-track": {
                 bgcolor: "#1a1a1a",
                borderRadius: borderRadius,
               },
               "&::-webkit-scrollbar-thumb": {
                background: "linear-gradient(135deg, #fbc2eb 0%, #ff9a9e 100%)",
                borderRadius: borderRadius,
                border: "1px solid rgba(255, 255, 255, 0.1)",
                 "&:hover": {
                  background: "linear-gradient(135deg, #ff9a9e 0%, #fbc2eb 100%)",
                 },
               },
             }}
          >
            {orderId && (
              <>
                <OrderDetailsFetcher
                  orderId={orderId}
                  onReload={(fn) => (reloadDetailsRef.current = fn)}
                  onClose={onClose}
                  onOrderDeleted={onOrderDeleted}
                />

                <InvoiceCreator
                  orderId={orderId}
                  onSuccess={() => {
                    if (onSuccess) onSuccess();
                    if (onClose) onClose();
                    navigate("/pos");
                  }}
                />
              </>
            )}
          </Box>

          {/* زر النزول للأسفل */}
          <Box
            sx={{
              position: "absolute",
              left: isXSmallTablet ? 8 : isSmallTablet ? 10 : 12,
              bottom: isXSmallTablet ? 8 : isSmallTablet ? 10 : 12,
              zIndex: 50,
               borderRadius: "50%",
             }}
          >
            <IconButton
              size={isSmallTablet ? "small" : "medium"}
              color="primary"
              onClick={handleScrollDown}
              sx={{
                bgcolor: "#fbc2eb",
                background: "linear-gradient(135deg, #fbc2eb 0%, #ff9a9e 100%)",
                color: "#232334",
                width: scrollButtonSize,
                height: scrollButtonSize,
                minWidth: scrollButtonSize,
                minHeight: scrollButtonSize,
                boxShadow: "0 4px 16px rgba(251, 194, 235, 0.4)",
                transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": { 
                  bgcolor: "#ff9a9e",
                  background: "linear-gradient(135deg, #ff9a9e 0%, #fbc2eb 100%)",
                  transform: "scale(1.1)",
                  boxShadow: "0 6px 16px rgba(251, 194, 235, 0.4)",
                  boxShadow: "0 6px 24px rgba(251, 194, 235, 0.6)",
                },
                "&:active": {
                  transform: "scale(0.95)",
                },
              }}
            >
              <KeyboardArrowDownIcon 
                sx={{ fontSize: isSmallTablet ? 20 : 24 }} 
                

              />
            </IconButton>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default TableModalContent;

